import classNames from "classnames"
import {
  createContext,
  ElementType,
  forwardRef,
  isValidElement,
  MouseEvent,
  ReactNode,
  useContext,
  useMemo,
} from "react"
import { Button } from "../Button"
import { IconButton } from "../IconButton"
import { deepFind } from "../utils/deepFind"
import { validateDisplayNameChild } from "../utils/validateDisplayNameChild"
import type { PolymorphicRef } from "../Box"
import type {
  PaginationControlsPropsWithRef,
  PaginationPagePropsWithRef,
  PaginationCountProps,
  PaginationPageProps,
  PaginationPagesProps,
  PaginationProps,
  PaginationContextValue,
} from "./types"
import { noop } from "lodash-es"
import { twMerge } from "tailwind-merge"

const PaginationContext = createContext<PaginationContextValue>({
  currentPage: 1,
  limitValue: 15,
  totalCount: 15,
  totalPages: 1,
  onPageChange: noop,
})

const PAGE_DEFAULT_ELEMENT = "a" as const
const CONTROL_DEFAULT_ELEMENT = "button" as const

const PaginationPage: (<E extends ElementType = typeof PAGE_DEFAULT_ELEMENT>(
  props: PaginationPagePropsWithRef<E>,
) => ReactNode | null) & { displayName?: string } = forwardRef(
  <E extends ElementType = typeof PAGE_DEFAULT_ELEMENT>(
    { children, label, number, ...rest }: PaginationPagePropsWithRef<E>,
    ref: PolymorphicRef,
  ) => {
    const { currentPage } = useContext(PaginationContext)
    const _className = classNames(
      "aria-[current=true]:bg-primary-lighten-100 aria-[current=true]:border-primary-darken-100 aria-[current]:px-2",
    )
    const isActive = number === currentPage
    return (
      <Button
        as={PAGE_DEFAULT_ELEMENT}
        href="#"
        {...rest}
        ref={ref}
        aria-label={label}
        aria-current={isActive}
        className={_className}
        size="sm"
        variation="borderless"
        isFullWidth
      >
        {number}
      </Button>
    )
  },
)

const PaginationPages = forwardRef<HTMLUListElement, PaginationPagesProps>(
  ({ label = () => "Go to page", render }, ref) => {
    const { currentPage, totalPages, onPageChange } = useContext(PaginationContext)
    const pages = useMemo(() => {
      const pageNumbers = []
      const maxVisiblePages = 5

      if (typeof currentPage === "undefined" && typeof totalPages === "undefined") return []

      let startPage = Math.max(1, Number(currentPage) - Math.floor(maxVisiblePages / 2))
      let endPage = startPage + maxVisiblePages - 1

      if (endPage > Number(totalPages)) {
        endPage = Number(totalPages)
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }

      if (startPage > 1) {
        pageNumbers.push({
          number: 1,
          key: 1,
          isEllipsis: false,
        })
      }

      if (startPage > 2) {
        pageNumbers.push({
          number: null,
          key: `${startPage}-ellipsis`,
          isEllipsis: true,
        })
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push({
          number: i,
          key: i,
          isEllipsis: false,
        })
      }

      if (endPage < Number(totalPages) - 1) {
        pageNumbers.push({
          number: null,
          key: `${endPage}-ellipsis`,
          isEllipsis: true,
        })
      }

      if (endPage < Number(totalPages)) {
        pageNumbers.push({
          number: totalPages,
          key: totalPages,
          isEllipsis: false,
        })
      }

      return pageNumbers
    }, [currentPage, totalPages])

    return (
      <ul className="flex" ref={ref}>
        {pages.map(({ number, isEllipsis, key }) => (
          <li key={key} className="min-w-8">
            {!isEllipsis && !render && number && (
              <PaginationPage
                label={number && label ? label(number) : undefined}
                number={number}
                onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault()
                  return onPageChange?.(Number(number))
                }}
              />
            )}
            {!isEllipsis && render && number && render(number)}
            {isEllipsis && (
              <span className="flex h-full w-full items-center justify-center text-center">
                ...
              </span>
            )}
          </li>
        ))}
      </ul>
    )
  },
)
PaginationPages.displayName = "Pagination.Pages"

const PaginationPrevious: (<E extends ElementType = typeof CONTROL_DEFAULT_ELEMENT>(
  props: PaginationControlsPropsWithRef<E>,
) => ReactNode | null) & { displayName?: string } = forwardRef(
  <E extends ElementType = typeof CONTROL_DEFAULT_ELEMENT>(
    props: Omit<PaginationPageProps<E>, "ref">,
    ref: PolymorphicRef,
  ) => {
    const { currentPage, onPageChange } = useContext(PaginationContext)
    const { label = "Previous page", onClick, as: Component, ...rest } = props

    const isDisabled = currentPage === 1
    return (
      <IconButton
        {...rest}
        as={Component || CONTROL_DEFAULT_ELEMENT}
        ref={ref}
        size="sm"
        variation="borderless"
        aria-label={label}
        iconName="ChevronLeft"
        disabled={isDisabled}
        // TODO: `aria-disabled` should be set internally by IconButton
        aria-disabled={isDisabled}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (!isDisabled) {
            onPageChange?.(Number(currentPage) - 1)
            onClick?.(event)
          }
        }}
      />
    )
  },
)
PaginationPrevious.displayName = "Pagination.Previous"

const PaginationNext: (<E extends ElementType = typeof CONTROL_DEFAULT_ELEMENT>(
  props: PaginationControlsPropsWithRef<E>,
) => ReactNode | null) & { displayName?: string } = forwardRef(
  <E extends ElementType = typeof CONTROL_DEFAULT_ELEMENT>(
    props: Omit<PaginationPageProps<E>, "ref">,
    ref: PolymorphicRef,
  ) => {
    const { currentPage, totalPages, onPageChange } = useContext(PaginationContext)
    const { label = "Next page", onClick, as: Component, ...rest } = props
    const isDisabled = currentPage === totalPages
    return (
      <IconButton
        {...rest}
        as={Component || CONTROL_DEFAULT_ELEMENT}
        ref={ref}
        size="sm"
        variation="borderless"
        aria-label={label}
        iconName="ChevronRight"
        disabled={isDisabled}
        // TODO: `aria-disabled` should be set internally by IconButton
        aria-disabled={isDisabled}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (!isDisabled) {
            onPageChange?.(Number(currentPage) + 1)
            onClick?.(event)
          }
        }}
      />
    )
  },
)
PaginationNext.displayName = "Pagination.Next"

function PaginationCount({ label }: PaginationCountProps) {
  const { currentPage, limitValue, totalCount } = useContext(PaginationContext)
  if (!label) return null

  const firstRecordPosition =
    currentPage && limitValue ? (currentPage - 1) * limitValue + 1 : undefined
  const lastRecordPosition =
    currentPage && limitValue && totalCount && Math.min(currentPage * limitValue, totalCount)

  return (
    <div className="min-w-fit text-center text-gray-400">
      {label({ currentPage, limitValue, totalCount, firstRecordPosition, lastRecordPosition })}
    </div>
  )
}

PaginationCount.displayName = "Pagination.Count"

export function Pagination({
  "aria-label": ariaLabel,
  className,
  children,
  currentPage = 1,
  limitValue = 15,
  totalCount = 15,
  onPageChange = noop,
  ...rest
}: PaginationProps) {
  const _prev = useMemo(
    () =>
      deepFind(children, child => {
        return isValidElement(child) && validateDisplayNameChild(child, "Pagination.Previous")
      }),
    [children],
  )
  const _next = useMemo(
    () =>
      deepFind(children, child => {
        return isValidElement(child) && validateDisplayNameChild(child, "Pagination.Next")
      }),
    [children],
  )
  const _pages = useMemo(
    () =>
      deepFind(children, child => {
        return isValidElement(child) && validateDisplayNameChild(child, "Pagination.Pages")
      }),
    [children],
  )
  const _count = useMemo(
    () =>
      deepFind(children, child => {
        return isValidElement(child) && validateDisplayNameChild(child, "Pagination.Count")
      }),
    [children],
  )

  const _className = classNames(
    twMerge(
      "sm:flex-row sm:justify-start flex flex-col justify-center items-center gap-y-4 gap-x-5",
      className,
    ),
  )

  const totalPages = Math.ceil(totalCount / limitValue)

  return (
    <PaginationContext.Provider
      value={{
        currentPage,
        limitValue,
        totalCount,
        totalPages,
        onPageChange,
      }}
    >
      <div className={_className} {...rest}>
        {_count}
        <nav className="flex w-full justify-center sm:w-auto " aria-label={ariaLabel}>
          {_prev}
          {_pages}
          {_next}
        </nav>
      </div>
    </PaginationContext.Provider>
  )
}
Pagination.displayName = "Pagination"

Pagination.Pages = PaginationPages
Pagination.Page = PaginationPage
Pagination.Previous = PaginationPrevious
Pagination.Next = PaginationNext
Pagination.Count = PaginationCount
